import { IntlShape } from 'react-intl';

export const VARIABLE_NODE_CLASS_NAME = 'email_template.variable';

/**
 * Placeholder as stored in the database, e.g. {{activity_link}}.
 */
const PLACEHOLDER_PATTERN = '{{\\s*([\\w.-]+)\\s*}}';

/**
 * Variables whose translation contains an ICU argument. Without the argument
 * react-intl fails to format the message and falls back to the raw pattern.
 */
const VARIABLE_LABEL_ARGUMENTS: Record<string, (intl: IntlShape) => Record<string, string>> = {
    managing_organization_support_email: (intl) => ({
        reseller: intl.formatMessage({ id: 'emailing.reseller' }),
    }),
    managing_organization_support_phone: (intl) => ({
        reseller: intl.formatMessage({ id: 'emailing.reseller' }),
    }),
    distributor_support_phone: (intl) => ({
        distributor: intl.formatMessage({ id: 'emailing.distributor' }),
    }),
};

export const translateVariable = (variable: string, intl: IntlShape): string =>
    intl.formatMessage(
        { id: `email_template.variable.${variable}` },
        VARIABLE_LABEL_ARGUMENTS[variable]?.(intl)
    );

const parseDocument = (html: string): Document =>
    new DOMParser().parseFromString(html, 'text/html');

/**
 * DOMParser always wraps the input into html/head/body, so a fragment has to be
 * serialized back without gaining a wrapper it never had. Leading style/link/meta
 * elements land in head, hence they are put back in front of the body content.
 */
const serialize = (doc: Document, source: string): string => {
    const isFullDocument = /<(html|head|body)[\s>]/i.test(source) || /<!doctype/i.test(source);

    if (!isFullDocument) {
        return doc.head.innerHTML + doc.body.innerHTML;
    }

    const doctype = doc.doctype ? `<!DOCTYPE ${doc.doctype.name}>` : '';

    return doctype + doc.documentElement.outerHTML;
};

/**
 * Every place that inserts a variable wraps its label in braces, so a node holding
 * anything else was left behind malformed by the previous parser — as were unclosed
 * nodes, which the HTML parser turns into nested ones. Label and content cannot be
 * told apart there, so such a node is left exactly as it is for a human to sort out.
 */
const isConvertible = (node: Element): boolean =>
    /^\s*{{[^{}]*}}\s*$/.test(node.textContent ?? '') &&
    !node.parentElement?.closest('[data-variable]') &&
    !node.querySelector('[data-variable]');

/**
 * Editor markup -> database template: every variable node becomes its {{name}}
 * placeholder again. Works off data-variable, so unrelated spans (colour, font
 * size) and the label language are irrelevant.
 */
export const variableNodesToPlaceholders = (html: string): string => {
    const doc = parseDocument(html);

    doc.querySelectorAll(`span[data-variable]`).forEach((node) => {
        if (!isConvertible(node)) {
            return;
        }

        const variable = node.getAttribute('data-variable');

        node.replaceWith(
            doc.createTextNode(variable ? `{{${variable}}}` : node.textContent ?? '')
        );
    });

    return serialize(doc, html);
};

const buildVariableNode = (doc: Document, variable: string, intl: IntlShape): HTMLElement => {
    const node = doc.createElement('span');

    node.setAttribute('data-variable', variable);
    node.setAttribute('class', VARIABLE_NODE_CLASS_NAME);
    node.textContent = `{{${translateVariable(variable, intl)}}}`;

    return node;
};

const replacePlaceholdersInTextNode = (node: Text, doc: Document, intl: IntlShape): void => {
    const pattern = new RegExp(PLACEHOLDER_PATTERN, 'g');
    const fragment = doc.createDocumentFragment();
    const text = node.data;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            fragment.appendChild(doc.createTextNode(text.slice(lastIndex, match.index)));
        }

        fragment.appendChild(buildVariableNode(doc, match[1], intl));
        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
    }

    node.replaceWith(fragment);
};

/**
 * Database template -> editor markup: placeholders found in text become labelled
 * variable nodes. Attribute values are never touched, so href="{{activity_link}}"
 * and the like survive the round trip.
 */
export const placeholdersToVariableNodes = (html: string, intl: IntlShape): string => {
    const doc = parseDocument(html);
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const placeholderNodes: Text[] = [];
    const placeholder = new RegExp(PLACEHOLDER_PATTERN);

    while (walker.nextNode()) {
        const node = walker.currentNode as Text;
        const parentTag = node.parentElement?.tagName;

        if (parentTag === 'STYLE' || parentTag === 'SCRIPT') {
            continue;
        }

        // Text inside a variable node is its label, so it must not be wrapped again.
        if (node.parentElement?.closest('[data-variable]')) {
            continue;
        }

        if (placeholder.test(node.data)) {
            placeholderNodes.push(node);
        }
    }

    placeholderNodes.forEach((node) => replacePlaceholdersInTextNode(node, doc, intl));

    return serialize(doc, html);
};
